import { Box, SvgIcon } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import AlertCircleIcon from 'mdi-react/AlertCircleIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import DoneAllIcon from 'mdi-react/DoneAllIcon';
import DoneIcon from 'mdi-react/DoneIcon';
import moment from 'moment';
import { any, object } from 'prop-types';
import React from 'react';
import { getMessageType, MESSAGE_TYPES } from 'utils/getMessageType';
import { I18n } from 'utils/i18n';
import isNumeric from 'utils/isNumeric';
import BaseMessage from './BaseMessage';
import Caption from './Caption';
import MessageAvatar from './MessageAvatar';
import ReactionCounter from './ReactionCounter';
import ReactionsButton from './ReactionsButton';

const Message = ({ message, channel, children }) => {
  const {
    temp,
    nameNotBlank: name,
    avatar,
    attachments,
    softDeletedAt,
    createdAt,
    isMine,
    error,
  } = message;
  const messageType = getMessageType(message);
  const loaded = message.isMine && !message.temp;
  const seen = message.isMine && message.seen;
  const loading = message.temp;

  const date = createdAt && isNumeric(createdAt) ? moment.unix(createdAt) : moment(createdAt);
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        pb: '10px',
        ...(isMine && {
          flexDirection: 'row-reverse',
          alignSelf: 'flex-end',
        }),
        '&:hover > .message-reactions': {
          display: 'flex',
        },
      }}
    >
      {!message.article && <MessageAvatar right={isMine} name={name} avatar={avatar} />}
      <BaseMessage
        right={isMine}
        attachments={!isEmpty(attachments)}
        isMandatory={
          !message.softDeletedAt && (message?.isMandatory || message?.form || message?.article)
        }
        message={message}
        channel={channel}
      >
        {children}
        {message.reactions?.length > 0 && (
          <Box
            sx={theme => ({
              display: 'flex',
              flexWrap: 'wrap',
              pl: 1.5,
              pb: 1,
              backgroundColor:
                messageType === MESSAGE_TYPES.ARTICLE ? theme.palette.background.paper : 'inherit',
              ...([MESSAGE_TYPES.IMAGE, MESSAGE_TYPES.VIDEO].includes(getMessageType(message)) && {
                pt: 1,
              }),
            })}
          >
            {message.reactions.map(reaction => (
              <ReactionCounter
                key={reaction.name}
                messageId={message._id}
                reaction={reaction.name}
                count={reaction.count}
                active={reaction.hasReacted}
                canSeeDetails={channel?.isAdmin || !channel?.isReadOnly}
              />
            ))}
          </Box>
        )}
      </BaseMessage>
      {!message.article && (
        <Caption
          right={isMine}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {message?.isEdited && !softDeletedAt ? (
            <Box component="span" sx={{ marinRight: '0.5em' }}>
              {I18n.t('Message.edited')}
            </Box>
          ) : (
            ''
          )}
          {!loaded && !seen && !error && loading && (
            <SvgIcon sx={{ fontSize: 12, opacity: 0.8 }}>
              <ClockOutlineIcon />
            </SvgIcon>
          )}
          {loaded && !error && !seen && !softDeletedAt && (
            <SvgIcon sx={{ fontSize: 12, opacity: 0.8 }}>
              <DoneIcon />
            </SvgIcon>
          )}
          {seen && !softDeletedAt && (
            <SvgIcon sx={{ ml: 0.25, fontSize: 12, opacity: 0.8 }}>
              <DoneAllIcon />
            </SvgIcon>
          )}
          {error && (
            <SvgIcon color="error" sx={{ ml: 0.25, fontSize: 12 }}>
              <AlertCircleIcon />
            </SvgIcon>
          )}
          {!temp && (
            <Box component="span" sx={{ ml: '0.5em' }}>
              {date.format('LT')}
            </Box>
          )}
        </Caption>
      )}
      <Box className="message-reactions" sx={{ display: 'none', alignSelf: 'center' }}>
        <ReactionsButton message={message} tabIndex={0} role="button" />
      </Box>
    </Box>
  );
};

Message.propTypes = {
  message: object.isRequired,
  children: any.isRequired,
  channel: object.isRequired,
};

export default Message;
